/* eslint-disable @typescript-eslint/no-magic-numbers */
// eslint-disable-next-line boundaries/element-types
import { FootPrintPadHoleType, FootPrintPadShape, FootPrintShapeType, ZoneConnectionType, } from "@buildwithflux/models";
// eslint-disable-next-line boundaries/element-types
import SparkMD5 from "spark-md5";
// eslint-disable-next-line boundaries/element-types
import { KicadPadShape, KicadPadType, } from "../../../..";
// eslint-disable-next-line boundaries/element-types
import { Clipper, JoinType } from "../../../../../../clipper";
// eslint-disable-next-line boundaries/element-types
import { KicadImportTextPlaceholder } from "../../../../../../SharedDataModels";
// eslint-disable-next-line boundaries/element-types
import { KiCadHelpers } from "../../KiCadHelpers";
const fluxOrigin = { x: 0, y: 0 };
const isKicadHoleType = (padType) => {
    return padType === KicadPadType.nonPlatedThruHole || padType === KicadPadType.thruHole;
};
const kicadPadHoleTypeToFluxHoleType = (holeType) => {
    switch (holeType) {
        case KicadPadType.nonPlatedThruHole:
            return FootPrintPadHoleType.nonPlatedHole;
        case KicadPadType.thruHole:
            return FootPrintPadHoleType.platedThroughHole;
    }
};
const intoMeters = (value) => {
    return value / 1000;
};
const kicadPointToFluxPoint = (point, angleInRadians) => {
    // TODO: do we still need the rotation by pi/2 here?  I don't think so...
    return { x: intoMeters(point.x), y: -1 * intoMeters(point.y), orientation: angleInRadians };
};
const padPositionToFluxPosition = (point) => {
    const padAngle = point.angle ? KiCadHelpers.degreesToRadians(point.angle) : 0;
    return kicadPointToFluxPoint(point, padAngle);
};
const kicadSizeToFluxSize = (size) => {
    return { x: intoMeters(size.width), y: intoMeters(size.height) };
};
const kicadPrimitivesToFluxVectors = (pad) => {
    if (!pad.primitives) {
        return [];
    }
    const vectors = pad.primitives
        .reduce((polygons, primitive) => {
        polygons.push(
        // Scale shapes first
        Clipper.shape([
            primitive.pts.map((point) => ({
                x: intoMeters(point.x),
                y: intoMeters(point.y),
            })),
        ])
            // Then offset
            .offset(intoMeters(primitive.width / 2), {
            joinType: JoinType.Round,
        })
            .toShape(0)
            // Finally obtain points
            .extractPoints(128)
            .shape.map((point) => ({ x: point.x, y: point.y })));
        return polygons;
    }, [])
        .reduce((mergedShape, polygon) => {
        const shape = Clipper.shape([polygon], true);
        if (!mergedShape) {
            mergedShape = shape;
        }
        else {
            mergedShape = mergedShape.intersect(shape);
        }
        return mergedShape;
    }, null)
        ?.toPolygons()
        .flatMap((points) => points);
    return vectors ?? [];
};
const cartesianDistance = (p1, p2) => {
    const dx = p2.x - p1.x;
    const dy = p2.y - p1.y;
    return Math.sqrt(dx * dx + dy * dy);
};
const kicadPadToFluxPad = (kicadPadDefinition, moduleHash) => {
    switch (kicadPadDefinition.pad_shape) {
        case KicadPadShape.circle:
            return kicadCircularPadToFluxPad(kicadPadDefinition, moduleHash);
        case KicadPadShape.custom:
            return kicadPolygonPadToFluxPad(kicadPadDefinition, moduleHash);
        case KicadPadShape.oval:
            return kicadOvalPadToFluxPad(kicadPadDefinition, moduleHash);
        case KicadPadShape.rectangle:
            return kicadRectangularPadToFluxPad(kicadPadDefinition, moduleHash);
        case KicadPadShape.roundRect:
            return kicadRectangularPadToFluxPad(kicadPadDefinition, moduleHash);
        case KicadPadShape.trapezoid:
            return kicadTrapezoidalPadToFluxPad(kicadPadDefinition, moduleHash);
    }
};
const kicadCircularPadToFluxPad = (pad, moduleHash) => {
    const basePadData = {
        shape: FootPrintPadShape.circular,
        position: pad.at ? padPositionToFluxPosition(pad.at) : fluxOrigin,
        size: kicadSizeToFluxSize(pad.size),
        name: pad.pad_id,
        uid: SparkMD5.hash(moduleHash + JSON.stringify(pad)),
    };
    switch (pad.pad_type) {
        case KicadPadType.surfaceMountDevice:
            return basePadData;
        case KicadPadType.nonPlatedThruHole:
            return addHoleToFluxPadFromKicadDefinition(basePadData, pad.pad_type, pad);
        case KicadPadType.thruHole:
            return addHoleToFluxPadFromKicadDefinition(basePadData, pad.pad_type, pad);
        case KicadPadType.connect:
            return basePadData;
    }
};
const addHoleToFluxPadFromKicadDefinition = (fluxPad, kicadHoleType, kicadPad) => {
    const holeType = kicadPadHoleTypeToFluxHoleType(kicadHoleType);
    const holeParameters = getFluxHoleParametersFromKicadPad(kicadPad);
    const result = {
        ...fluxPad,
        holeType: holeType,
        holeSize: holeParameters.size,
        holePosition: holeParameters.offset,
        holeMask: holeParameters.mask,
    };
    return result;
};
const getFluxHoleParametersFromKicadPad = (pad) => {
    const holeSize = getHoleSizeIfExists(pad) ?? computeSaneDefaultHoleSize(pad);
    const holeOffset = getHoleOffsetIfExists(pad) ?? fluxOrigin;
    const holeMask = "00E0FFFF"; // NOTE: pulled this from SharedDataModels.ts.
    return {
        size: holeSize,
        offset: holeOffset,
        mask: holeMask,
    };
};
const getHoleOffsetIfExists = (pad) => {
    // TODO: For an oval hole, we will need to factor in a possible rotation angle
    return pad.drill?.offset ? kicadPointToFluxPoint(pad.drill.offset) : undefined;
};
function circleOfRadius(radius) {
    return { x: radius, y: radius };
}
const computeSaneDefaultHoleSize = (pad) => {
    // TODO: what's a better way to do this?
    return circleOfRadius(intoMeters(Math.min(pad.size.height, pad.size.width) / 2));
};
const getHoleSizeIfExists = (pad) => {
    if (pad.drill) {
        if (pad.drill.height && pad.drill.width) {
            return kicadSizeToFluxSize({
                height: pad.drill.height,
                width: pad.drill.width,
            });
        }
        else if (pad.drill.height) {
            return circleOfRadius(intoMeters(pad.drill.height));
        }
    }
    return undefined;
};
const kicadPolygonPadToFluxPad = (pad, moduleHash) => {
    return {
        shape: FootPrintPadShape.polygon,
        size: kicadSizeToFluxSize(pad.size),
        name: pad.pad_id,
        position: pad.at ? padPositionToFluxPosition(pad.at) : fluxOrigin,
        uid: SparkMD5.hash(moduleHash + JSON.stringify(pad)),
        vertices: kicadPrimitivesToFluxVectors(pad),
    };
};
const kicadOvalPadToFluxPad = (pad, moduleHash) => {
    // NB: Kicad refers to this as an "oval".  An oval is a pretty squishy definition, so
    // we will call it an ellipse.  This should be adequate, but worth bearing in mind that an
    // oval has no formal definition!  Based on the data KiCad provides, they shouldn't be able
    // to draw any oval that _isn't_ an ellipse, so this should be safe.  Any more complex shape
    // will just be a polygon.
    const basePadData = {
        shape: FootPrintPadShape.ellipse,
        position: pad.at ? padPositionToFluxPosition(pad.at) : fluxOrigin,
        name: pad.pad_id,
        size: kicadSizeToFluxSize(pad.size),
        uid: SparkMD5.hash(moduleHash + JSON.stringify(pad)),
    };
    switch (pad.pad_type) {
        case KicadPadType.surfaceMountDevice:
            return basePadData;
        case KicadPadType.nonPlatedThruHole:
            return addHoleToFluxPadFromKicadDefinition(basePadData, pad.pad_type, pad);
        case KicadPadType.thruHole:
            return addHoleToFluxPadFromKicadDefinition(basePadData, pad.pad_type, pad);
        case KicadPadType.connect:
            return basePadData;
    }
};
const kicadRectangularPadToFluxPad = (pad, moduleHash) => {
    const padShape = getRectangularPadVariety(pad.pad_shape);
    const baseShape = {
        shape: padShape,
        name: pad.pad_id,
        size: kicadSizeToFluxSize(pad.size),
        position: pad.at ? padPositionToFluxPosition(pad.at) : fluxOrigin,
        uid: SparkMD5.hash(moduleHash + JSON.stringify(pad)),
    };
    let longerSide = pad.size.height;
    if (pad.size.width > longerSide)
        longerSide = pad.size.width;
    if (baseShape.shape === FootPrintPadShape.obround)
        baseShape.cornerRadius = getCornerRadiusIfExists(pad) ?? computeSaneDefaultCornerRadius(longerSide);
    return baseShape;
};
const getCornerRadiusIfExists = (pad) => {
    return pad.roundrect_rratio ? intoMeters(pad.size.width) * pad.roundrect_rratio : undefined;
};
const computeSaneDefaultCornerRadius = (value) => {
    // TODO: does this seem reasonable?  This basically establishes a default radius ratio of 0.075
    return intoMeters(value) * 0.075;
};
const getRectangularPadVariety = (padShape) => {
    switch (padShape) {
        case KicadPadShape.rectangle:
            return FootPrintPadShape.rectangle;
        case KicadPadShape.roundRect:
            return FootPrintPadShape.obround;
        // TODO: remove me when we support polygon
        case KicadPadShape.custom:
            return FootPrintPadShape.polygon;
        // TODO: this is dumb, we should try to narrow the type more before we get here
        // QUESTION: are there any known instances of this default case?
        default:
            return FootPrintPadShape.rectangle;
    }
};
const kicadTrapezoidalPadToFluxPad = (pad, moduleHash) => {
    return {
        shape: FootPrintPadShape.trapezoid,
        name: pad.pad_id,
        size: kicadSizeToFluxSize(pad.size),
        position: pad.at ? padPositionToFluxPosition(pad.at) : fluxOrigin,
        delta: fluxOrigin, // TODO: what does this parameter even mean?
        uid: SparkMD5.hash(moduleHash + JSON.stringify(pad)),
    };
};
const kicadFootprintArcToFlux = (arc, moduleHash) => {
    return {
        type: FootPrintShapeType.arc,
        start: kicadPointToFluxPoint(arc.start),
        mid: kicadPointToFluxPoint(arc.mid),
        end: kicadPointToFluxPoint(arc.end),
        strokeWidth: intoMeters(arc.width),
        layerUid: arc.layer,
        uid: SparkMD5.hash(moduleHash + JSON.stringify(arc)),
    };
};
const kicadFootprintCircleToFlux = (circle, moduleHash) => {
    return {
        type: FootPrintShapeType.circle,
        position: padPositionToFluxPosition(circle.center),
        radius: cartesianDistance(kicadPointToFluxPoint(circle.center), kicadPointToFluxPoint(circle.end)),
        strokeWidth: intoMeters(circle.width),
        layerUid: circle.layer,
        uid: SparkMD5.hash(moduleHash + JSON.stringify(circle)),
    };
};
const kicadFootprintLineToFlux = (line, moduleHash) => {
    return {
        type: FootPrintShapeType.line,
        start: kicadPointToFluxPoint(line.start),
        end: kicadPointToFluxPoint(line.end),
        strokeWidth: intoMeters(line.width),
        layerUid: line.layer,
        uid: SparkMD5.hash(moduleHash + JSON.stringify(line)),
    };
};
const isCopperLayer = (layer) => {
    if (!layer)
        return false;
    const lower = layer.toLowerCase();
    return lower.includes(".cu");
};
const isMaskOrPasteLayer = (layer) => {
    if (!layer)
        return false;
    const lower = layer.toLowerCase();
    return lower.includes("mask") || lower.includes("paste");
};
const kicadFootprintPolyToFluxShape = (polygon, moduleHash) => {
    return {
        type: FootPrintShapeType.polygon,
        vertices: (polygon.pts ?? []).map((pt) => kicadPointToFluxPoint(pt)),
        strokeWidth: intoMeters(polygon.width ?? 0),
        uid: SparkMD5.hash(moduleHash + JSON.stringify(polygon)),
        layerUid: polygon.layer ?? "F.SilkS",
    };
};
const kicadFootprintPolyToFluxPad = (polygon, moduleHash) => {
    const vertices = (polygon.pts ?? []).map((pt) => ({
        x: intoMeters(pt.x),
        y: intoMeters(pt.y),
    }));
    return {
        shape: FootPrintPadShape.polygon,
        vertices,
        size: { x: 0, y: 0 },
        name: "",
        position: { x: 0, y: 0 },
        uid: SparkMD5.hash(moduleHash + JSON.stringify(polygon)),
    };
};
const kicadFootprintModelToFlux = (model, moduleHash) => {
    return {
        type: FootPrintShapeType.model,
        uid: SparkMD5.hash(moduleHash + JSON.stringify(model)),
        modelPath: model.model_path,
        position: model.at,
        offset: model.offset,
        scale: model.scale,
        rotate: model.rotate,
    };
};
const kicadFootprintTextToFlux = (text, moduleName, moduleHash) => {
    const fluxText = convertKicadTextToFluxText(text.text, moduleName);
    return {
        type: FootPrintShapeType.text,
        position: text.at ? kicadPointToFluxPoint(text.at) : fluxOrigin,
        size: kicadSizeToFluxSize(text.effects.font.size),
        italic: text.effects.italic ?? false,
        text: fluxText,
        strokeWidth: intoMeters(text.effects.font.thickness),
        uid: SparkMD5.hash(moduleHash + JSON.stringify(text)),
        layerUid: text.layer, // QUESTION: is this correct?  Are the flux layers identifiable this way?
        kicadTextType: text.type,
    };
};
const convertKicadTextToFluxText = (kicadText, moduleName) => {
    if (kicadText === "REF**" || kicadText === "%R" || kicadText === "${REFERENCE}")
        return KicadImportTextPlaceholder.refReplace;
    if (moduleName.endsWith(kicadText))
        return KicadImportTextPlaceholder.valueReplace;
    return kicadText;
};
const getFluxShapes = (footprint, moduleHash) => {
    const arcs = footprint.fp_arc ?? [];
    const circles = footprint.fp_circle ?? [];
    const lines = footprint.fp_line ?? [];
    const graphicalPolys = (footprint.fp_poly ?? []).filter((p) => !isCopperLayer(p.layer) && !isMaskOrPasteLayer(p.layer));
    const texts = footprint.fp_text ?? [];
    const models = footprint.model ?? [];
    return [
        arcs.map((arc) => kicadFootprintArcToFlux(arc, moduleHash)),
        circles.map((circle) => kicadFootprintCircleToFlux(circle, moduleHash)),
        lines.map((line) => kicadFootprintLineToFlux(line, moduleHash)),
        graphicalPolys.map((poly) => kicadFootprintPolyToFluxShape(poly, moduleHash)),
        texts.map((txt) => kicadFootprintTextToFlux(txt, footprint.name, moduleHash)),
        models.map((model) => kicadFootprintModelToFlux(model, moduleHash)),
    ].flat();
};
const getFluxPadsFromPolygons = (footprint, moduleHash) => {
    const copperPolys = (footprint.fp_poly ?? []).filter((p) => isCopperLayer(p.layer));
    return copperPolys.map((poly) => kicadFootprintPolyToFluxPad(poly, moduleHash));
};
const kicadMechSpecsToFluxMechSpecs = (pad) => {
    const kicadData = {
        padToDieLength: pad.die_length,
        solderPaste: pad.solder_paste_margin,
        solderMask: pad.solder_mask_margin,
        localClearance: pad.clearance,
        solderPasteRatioPercentage: pad.solder_paste_margin_ratio,
        zoneConnection: pad.zone_connect ? kicadZoneConnectionToFluxZoneConnection(pad.zone_connect) : undefined,
        thermalGap: undefined,
        thermalWidth: undefined,
    };
    let fluxData = undefined;
    // eslint-disable-next-line @buildwithflux/no-array-foreach
    Object.entries(kicadData).forEach((entry) => {
        // Yikes, this is kind of a mess.  But there are currently limitations in the type system
        // that prevents Object.entries from being typesafe.  What we're trying to do is just make
        // sure that we don't include data that we don't need.
        // Would be nice to remove this once our model on the flux side is a little more advanced
        // and can better discriminate e.g. pads vs holes!
        const [name, value] = entry;
        if (value !== undefined) {
            if (fluxData === undefined)
                fluxData = {};
            fluxData = { ...fluxData, [name]: value };
        }
    });
    return fluxData;
};
const kicadZoneConnectionToFluxZoneConnection = (kicadZoneConnection) => {
    switch (kicadZoneConnection) {
        case 0:
            return ZoneConnectionType.padsNotCovered;
        case 1:
            return ZoneConnectionType.thermalRelief;
        case 2:
            return ZoneConnectionType.padSolidInsideTheZone;
        case 3:
            return ZoneConnectionType.thermalReliefOnlyForThPads;
    }
};
export const convertKicadFootprintToFluxFootprint = (footprint, moduleHash) => {
    const padDefinitions = footprint.pad?.map((pad) => {
        let fluxPad = kicadPadToFluxPad(pad, moduleHash);
        const mechanicalSpecs = kicadMechSpecsToFluxMechSpecs(pad);
        if (isKicadHoleType(pad.pad_type))
            fluxPad = addHoleToFluxPadFromKicadDefinition(fluxPad, pad.pad_type, pad);
        if (mechanicalSpecs)
            fluxPad = { ...fluxPad, ...mechanicalSpecs };
        return fluxPad;
    }) ?? [];
    const polygonPads = getFluxPadsFromPolygons(footprint, moduleHash);
    const shapeDefinitions = getFluxShapes(footprint, moduleHash);
    return {
        uid: SparkMD5.hash(moduleHash + JSON.stringify(footprint)),
        name: footprint.name,
        keywords: footprint.tags,
        pads: [...padDefinitions, ...polygonPads],
        shapes: shapeDefinitions,
    };
};
//# sourceMappingURL=KicadToFlux.js.map